import { useMemo } from "react"
import { useTranslation } from "react-i18next"
import moment from "moment-timezone"
import classNames from "classnames"

import { createAccountStore } from "@/stores/account"
import BasicButton from "@/components/button/BasicButton"
import Icon from "@/components/icon/Icon"
import { getImagePath } from "@/utils/image"
import Tooltip from "@/components/tooltip/Tooltip"
import { convertActivityCode, getDurationString, globalTime } from "@/utils/format"
import {
  ActivityDetail,
  ActivityParticipatedPlayer,
  ActivityType,
  TrendReportDataType,
} from "@/types/activity"
import { REPORT_DATE_FORMAT, TIME_FORMAT } from "@/constants/date"
import { TrendPlayerReport } from "@/types/report"
import { REPORT_TYPE } from "@/app/report/common/constants"

import "./informationSummary.scss"

export default function InformationSummary({
  type = REPORT_TYPE.DAILY,
  data,
  playersData,
  onClickExportPDF,
  isDialog = false,
}: {
  type?: REPORT_TYPE
  data:
    | ActivityDetail
    | (Omit<ActivityDetail, "type" | "matchDay"> & { type: string; matchDay: string })
    | TrendReportDataType
  playersData?: ActivityParticipatedPlayer[] | (TrendPlayerReport & { image: string | null })[]
  onClickExportPDF?: () => void
  isDialog?: boolean
}) {
  const { t } = useTranslation()
  const { teamProfileInfo } = createAccountStore()

  const isOnePlayer = playersData?.length === 1

  const getGlobalTime = (startTime: string, endTime: string, elapsedTime: number) => {
    const isCorrectDate = startTime !== "" && endTime !== ""

    return isCorrectDate
      ? `${globalTime(startTime)} - ${globalTime(endTime)} (${getDurationString(elapsedTime)})`
      : "-"
  }

  const getElapsedTime = (dateStart: string, dateFinish: string) => {
    return moment.duration(moment(dateFinish).diff(moment(dateStart))).asMinutes()
  }

  const activitySessionInfoArray = useMemo(() => {
    if (type === REPORT_TYPE.DAILY && "seasonWeek" in data) {
      return [
        {
          label: t("season"),
          value: data.seasonWeek,
        },
        {
          label: t("activityType"),
          value: t(convertActivityCode(data.type) as string),
        },
        {
          label: t("matchDayType"),
          value: data.matchDay,
        },
        {
          label: t("globalTime"),
          value:
            data.dateStart !== "-" && data.dateFinish !== "-"
              ? getGlobalTime(
                  data.dateStart,
                  data.dateFinish,
                  getElapsedTime(data.dateStart, data.dateFinish),
                )
              : "-",
        },
        {
          label: t("playerCount"),
          value: data.players.length,
        },
        {
          label: t("location"),
          value: data.stadium,
        },
      ]
    }
    if (type === REPORT_TYPE.TREND && "activityCount" in data) {
      if (isOnePlayer) {
        return [
          {
            label: t("activity"),
            value: data.activityCount,
          },
          ...Object.keys(ActivityType).map((key) => ({
            label: t(convertActivityCode(key) as string),
            value: data.activityKeyCount[key],
          })),
        ]
      }

      return [
        {
          label: t("playerCount"),
          value: data.playerCount,
        },
        {
          label: t("activity"),
          value: data.activityCount,
        },
        ...Object.keys(ActivityType).map((key) => ({
          label: t(convertActivityCode(key) as string),
          value: data.activityKeyCount[key],
        })),
      ]
    }

    return []
  }, [data, t, playersData])

  return (
    <div className={classNames("ReportInformationSummaryCont", { isDialog })}>
      <div className="report-info-team-logo">
        {isOnePlayer ? (
          <PlayerImage image={playersData[0].image ?? ""} />
        ) : (
          <TeamImage image={teamProfileInfo?.image} />
        )}
      </div>
      <div className="ReportInformationCont">
        <div className="TeamWithBtnCont">
          {isOnePlayer ? (
            <div className="team-activity-title">
              <p>{data?.name ?? "-"}</p>
              <h3>{`${playersData[0].uniformNumber} ${"name" in playersData[0] ? playersData[0].name : playersData[0].playerName}`}</h3>
            </div>
          ) : (
            <div className="team-activity-title">
              <p>{teamProfileInfo?.name ?? "-"}</p>
              <h3>{data?.name ?? "-"}</h3>
            </div>
          )}
          <div className="team-activity-button">
            {!isDialog &&
              type === REPORT_TYPE.DAILY &&
              "analyzeFinished" in data &&
              data.analyzeFinished !== "-" && (
                <Tooltip
                  type="text"
                  size="small"
                  title={`${t("lastAnalyzed")}: ${moment(data.analyzeFinished).format(`${REPORT_DATE_FORMAT} ${TIME_FORMAT}`)}`}
                  placement="top-end">
                  <Icon
                    type="information-fill"
                    size={16}
                    className="team-activity-information-icon"
                  />
                </Tooltip>
              )}
            {!isDialog && (
              <BasicButton type="default-outlined" size="sm" onClick={onClickExportPDF}>
                <span className="export-pdf-button">
                  <Icon type="share-box" size={16} />
                  <p>{t("button.export")}</p>
                </span>
              </BasicButton>
            )}
          </div>
        </div>
        <div className={classNames("ActivityDetailCont", type, { isOnePlayer })}>
          {activitySessionInfoArray.map((item, index) => (
            <div key={index} className="ActivityDetailItem">
              <p className="activity-detail-label">{item.label}</p>
              <h3 className="activity-detail-value">
                {type === REPORT_TYPE.DAILY ? (item.value ?? "-") : item.value}
              </h3>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

function TeamImage({ image }: { image: string | undefined }) {
  if (image) {
    return <img src={getImagePath(image)} alt="Team logo" width={48} height={48} />
  } else {
    return <Icon type="no-team" width={48} height={48} />
  }
}

function PlayerImage({ image }: { image: string | undefined }) {
  if (image) {
    return <img src={getImagePath(image)} alt="Player photo" width={48} height={48} />
  } else {
    return <Icon type="no-profile" width={48} height={48} />
  }
}
